import React, { useState, useEffect } from 'react';
import '@/Pages/admin/UserEngagement/userEngagementAnalytics.css';
import { getDataFromBrowser } from '@/utils/browserStorage';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

const UserEngagementAnalytics = () => {
  const [data, setData] = useState({
    overview: null,
    watchHourBadges: [],
    kickoffBadges: [],
    loginMilestones: null,
    generalMilestones: [],
    anniversaries: []
  });
  const [loading, setLoading] = useState(true);
  const [userSearch, setUserSearch] = useState('');
  const [userDetails, setUserDetails] = useState(null);
  const [userLoading, setUserLoading] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
      const baseUrl = import.meta.env.VITE_APP_API_URL;
      const token = localStorage.getItem('token')?.replace(/"/g, '');
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const [overview, watchHours, kickoff, login, general, anniversaries] = await Promise.all([
        fetch(`${baseUrl}/admin/engagement/overview`, { headers }).then(res => res.json()),
        fetch(`${baseUrl}/admin/engagement/watch-hours`, { headers }).then(res => res.json()),
        fetch(`${baseUrl}/admin/engagement/kickoff-badges`, { headers }).then(res => res.json()),
        fetch(`${baseUrl}/admin/engagement/login-milestones`, { headers }).then(res => res.json()),
        fetch(`${baseUrl}/admin/engagement/general-milestones`, { headers }).then(res => res.json()),
        fetch(`${baseUrl}/admin/engagement/anniversaries`, { headers }).then(res => res.json())
      ]);

      setData({
        overview: overview.data,
        watchHourBadges: watchHours.data,
        kickoffBadges: kickoff.data,
        loginMilestones: login.data,
        generalMilestones: general.data,
        anniversaries: anniversaries.data
      });
      setLoading(false);
  };

  const searchUser = async () => {
    if (!userSearch.trim()) return;
    
    setUserLoading(true);
    const baseUrl = import.meta.env.VITE_APP_API_URL;
    const token = localStorage.getItem('token')?.replace(/"/g, '');
      
    const response = await fetch(`${baseUrl}/admin/engagement/user/${encodeURIComponent(userSearch)}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
      
    const result = await response.json();
      
    if (result.success) {
      setUserDetails(result.data);
    }
    setUserLoading(false);
  };

  const formatBadgeName = (key) => {
    return key.replace(/([A-Z])/g, ' $1')
              .replace(/^./, str => str.toUpperCase())
              .replace(/([a-z])([A-Z])/g, '$1 $2');
  };

  if (loading) {
    return <div className="loading">Loading engagement analytics...</div>;
  }

  return (
    <div className="user-engagement-analytics">
      <h1>User Engagement & Milestones</h1>
      
      {/* User Search & Details */}
      <div className="section">
        <h2>User Details Search</h2>
        <div className="user-search">
          <input
            type="text"
            placeholder="Enter user email or ID..."
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && searchUser()}
          />
          <button onClick={searchUser} disabled={userLoading}>
            {userLoading ? 'Searching...' : 'Search'}
          </button>
        </div>

        {userDetails && (
          <div className="user-details">
            {userDetails.error ? (
              <div className="error">{userDetails.error}</div>
            ) : (
              <>
                <h3>User: {userDetails.email}</h3>
                <div className="user-summary">
                  <div className="summary-item">
                    <strong>Total Watch Hours:</strong> {userDetails.summary.totalWatchHours}
                  </div>
                  <div className="summary-item">
                    <strong>Videos Watched:</strong> {userDetails.summary.videosWatched}
                  </div>
                  <div className="summary-item">
                    <strong>Current Login Streak:</strong> {userDetails.summary.currentLoginStreak} days
                  </div>
                  <div className="summary-item">
                    <strong>Longest Login Streak:</strong> {userDetails.summary.longestLoginStreak} days
                  </div>
                  <div className="summary-item">
                    <strong>Member Since:</strong> {new Date(userDetails.summary.membershipStart).toLocaleDateString()} ({userDetails.summary.membershipDays} days)
                  </div>
                </div>

                <div className="badges-section">
                  <h4>Badge Status</h4>
                  <div className="badge-categories">
                    <div className="badge-category">
                      <h5>Kickoff Badges</h5>
                      {Object.entries(userDetails.badges.kickoffBadges).map(([key, data]) => (
                        <div key={key} className={`badge-item ${data?.achieved ? 'achieved' : 'not-achieved'}`}>
                          {formatBadgeName(key)}: {data?.achieved ? <CheckIcon /> : <CloseIcon />}
                          {data?.achieved && data?.date && (
                            <span className="badge-date"> ({new Date(data.date).toLocaleDateString()})</span>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="badge-category">
                      <h5>Watch Hour Milestones</h5>
                      {Object.entries(userDetails.badges.watchHourMilestones).map(([key, data]) => (
                        <div key={key} className={`badge-item ${data?.achieved ? 'achieved' : 'not-achieved'}`}>
                          {formatBadgeName(key)}: {data?.achieved ? <CheckIcon /> : <CloseIcon />}
                          {data?.achieved && data?.date && (
                            <span className="badge-date"> ({new Date(data.date).toLocaleDateString()})</span>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="badge-category">
                      <h5>Login Milestones</h5>
                      {Object.entries(userDetails.badges.loginMilestones).map(([key, data]) => (
                        <div key={key} className={`badge-item ${data?.achieved ? 'achieved' : 'not-achieved'}`}>
                          {formatBadgeName(key)}: {data?.achieved ? <CheckIcon /> : <CloseIcon />}
                          {data?.achieved && data?.date && (
                            <span className="badge-date"> ({new Date(data.date).toLocaleDateString()})</span>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="badge-category">
                      <h5>General Milestones</h5>
                      {Object.entries(userDetails.badges.generalMilestones).map(([key, data]) => (
                        <div key={key} className={`badge-item ${data?.achieved ? 'achieved' : 'not-achieved'}`}>
                          {formatBadgeName(key)}: {data?.achieved ? <CheckIcon /> : <CloseIcon />}
                          {data?.achieved && data?.date && (
                            <span className="badge-date"> ({new Date(data.date).toLocaleDateString()})</span>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="badge-category">
                      <h5>Anniversary Badges</h5>
                      {Object.entries(userDetails.badges.anniversaries).map(([key, data]) => (
                        <div key={key} className={`badge-item ${data?.achieved ? 'achieved' : 'not-achieved'}`}>
                          {formatBadgeName(key)}: {data?.achieved ? <CheckIcon /> : <CloseIcon />}
                          {data?.achieved && data?.date && (
                            <span className="badge-date"> ({new Date(data.date).toLocaleDateString()})</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      
      {/* Global Overview */}
      {data.overview && (
        <div className="overview-section">
          <h2>Global Overview</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Total Users</h3>
              <div className="stat-number">{data.overview.totalUsers.toLocaleString()}</div>
            </div>
            <div className="stat-card">
              <h3>Total Watch Hours</h3>
              <div className="stat-number">{data.overview.totalWatchHours.toLocaleString()}</div>
            </div>
            <div className="stat-card">
              <h3>Last 30 Days</h3>
              <div className="stat-number">{data.overview.last30DaysWatchHours.toLocaleString()}h</div>
            </div>
            <div className="stat-card">
              <h3>Avg Hours/User</h3>
              <div className="stat-number">{data.overview.avgWatchHours}</div>
            </div>
            <div className="stat-card">
              <h3>Avg Videos/User</h3>
              <div className="stat-number">{data.overview.avgVideosWatched}</div>
            </div>
            <div className="stat-card">
              <h3>Avg Login Streak</h3>
              <div className="stat-number">{data.overview.avgLoginStreak}</div>
            </div>
          </div>
        </div>
      )}

      {/* Watch Hour Badge Funnel */}
      <div className="section">
        <h2>Watch Hour Badge Distribution</h2>
        <table className="analytics-table">
          <thead>
            <tr>
              <th>Badge</th>
              <th>Users Achieved</th>
              <th>% of Users</th>
            </tr>
          </thead>
          <tbody>
            {data.watchHourBadges.map((badge, index) => (
              <tr key={index}>
                <td>{badge.name}</td>
                <td>{badge.usersAchieved}</td>
                <td>{badge.percentage}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Kickoff Badge Adoption */}
      <div className="section">
        <h2>Kickoff Badge Adoption</h2>
        <table className="analytics-table">
          <thead>
            <tr>
              <th>Badge</th>
              <th>Users Achieved</th>
              <th>% of Users</th>
            </tr>
          </thead>
          <tbody>
            {data.kickoffBadges.map((badge, index) => (
              <tr key={index}>
                <td>{badge.name}</td>
                <td>{badge.usersAchieved}</td>
                <td>{badge.percentage}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Login Milestones */}
      {data.loginMilestones && (
        <div className="section">
          <h2>Login Engagement</h2>
          <div className="login-stats">
            <div className="stat-item">
              <strong>Average Current Streak:</strong> {data.loginMilestones.avgCurrentStreak} days
            </div>
            <div className="stat-item">
              <strong>Platform Longest Streak:</strong> {data.loginMilestones.longestStreak} days
            </div>
          </div>
          <table className="analytics-table">
            <thead>
              <tr>
                <th>Milestone</th>
                <th>Users Achieved</th>
                <th>% of Users</th>
              </tr>
            </thead>
            <tbody>
              {data.loginMilestones.badges.map((badge, index) => (
                <tr key={index}>
                  <td>{badge.name}</td>
                  <td>{badge.usersAchieved}</td>
                  <td>{badge.percentage}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* General Activity Milestones */}
      <div className="section">
        <h2>General Activity Milestones</h2>
        <table className="analytics-table">
          <thead>
            <tr>
              <th>Milestone</th>
              <th>Users Achieved</th>
              <th>% of Users</th>
            </tr>
          </thead>
          <tbody>
            {data.generalMilestones.map((milestone, index) => (
              <tr key={index}>
                <td>{milestone.name}</td>
                <td>{milestone.usersAchieved}</td>
                <td>{milestone.percentage}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Membership Anniversaries */}
      <div className="section">
        <h2>Membership Anniversary Tracking</h2>
        <table className="analytics-table">
          <thead>
            <tr>
              <th>Anniversary</th>
              <th>Users Achieved</th>
            </tr>
          </thead>
          <tbody>
            {data.anniversaries.map((anniversary, index) => (
              <tr key={index}>
                <td>{anniversary.name}</td>
                <td>{anniversary.usersAchieved}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserEngagementAnalytics;